import React from "react";

// Illustrated 国潮 (guochao) hero backdrop: a full-bleed jade-and-gold teacup
// landscape illustration (with the 你好功德 signboard woven in) sits softly
// behind the hero text, layered with gentle animation — a breathing sun halo,
// rising tea-steam / cloud mist, a drifting water shimmer on the teacup lake,
// twinkling light sparks and falling petals. Purely decorative, pointer-events
// none, and disabled automatically under prefers-reduced-motion (index.css).

const PETALS = [
  { left: "14%", delay: "0s", dur: "12s", size: 13, color: "var(--brand-vermillion)" },
  { left: "28%", delay: "3.5s", dur: "13.5s", size: 10, color: "var(--brand-gold)" },
  { left: "44%", delay: "1.4s", dur: "11s", size: 12, color: "var(--brand-gold-soft)" },
  { left: "62%", delay: "5s", dur: "14s", size: 9, color: "var(--brand-vermillion)" },
  { left: "74%", delay: "2.2s", dur: "12.5s", size: 12, color: "var(--brand-gold)" },
  { left: "86%", delay: "6.2s", dur: "13s", size: 10, color: "var(--brand-gold-soft)" },
];

const SPARKS = [
  { left: "20%", top: "26%", delay: "0s" },
  { left: "40%", top: "16%", delay: "1.1s" },
  { left: "56%", top: "24%", delay: "0.6s" },
  { left: "70%", top: "20%", delay: "1.8s" },
  { left: "33%", top: "40%", delay: "2.4s" },
  { left: "80%", top: "36%", delay: "1.4s" },
];

const MIST = [
  { left: "42%", delay: "0s", dur: "9s", w: 90 },
  { left: "50%", delay: "3s", dur: "10.5s", w: 70 },
  { left: "57%", delay: "1.6s", dur: "9.6s", w: 82 },
];

const Petal = ({ size, color }) => (
  <svg width={size} height={size * 1.4} viewBox="0 0 20 28" fill="none" aria-hidden="true">
    <path
      d="M10 0C4 8 0 15 0 20c0 5 4 8 10 8s10-3 10-8c0-5-4-12-10-20z"
      fill={`hsla(${color},0.6)`}
    />
  </svg>
);

export const HeroGuanyinLotusBackdrop = () => {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {/* Full-bleed illustrated landscape */}
      <img src="/assets/hero-illustration.jpg" alt="" className="hero-illustration-img" />

      {/* Breathing sun halo over the illustration's sun (upper-center) */}
      <div
        className="halo-breathe absolute"
        style={{
          left: "50%", top: "6%", width: 300, height: 300, transform: "translateX(-50%)",
          background: "radial-gradient(circle, hsla(var(--brand-gold-soft),0.6) 0%, hsla(var(--brand-gold-soft),0) 68%)",
        }}
      />

      {/* Rising tea-steam / cloud mist */}
      {MIST.map((m, i) => (
        <div
          key={`mist-${i}`}
          className="mist-rise absolute top-[34%]"
          style={{ left: m.left, width: m.w, height: m.w * 1.6, animationDelay: m.delay, animationDuration: m.dur }}
        >
          <div
            className="h-full w-full rounded-full"
            style={{ background: "radial-gradient(ellipse at center, hsla(var(--brand-gold-soft),0.22) 0%, hsla(var(--brand-gold-soft),0) 72%)" }}
          />
        </div>
      ))}

      {/* Water shimmer drifting across the teacup lake (lower-center) */}
      <div
        className="water-shimmer absolute"
        style={{
          left: "34%", top: "56%", width: "32%", height: 40, borderRadius: 999,
          background: "linear-gradient(90deg, hsla(0,0%,100%,0) 0%, hsla(190,40%,88%,0.6) 50%, hsla(0,0%,100%,0) 100%)",
          filter: "blur(6px)",
        }}
      />

      {/* Twinkling light sparks */}
      {SPARKS.map((s, i) => (
        <span
          key={`spark-${i}`}
          className="spark-twinkle absolute rounded-full"
          style={{
            left: s.left, top: s.top, width: 5, height: 5,
            background: "hsl(var(--brand-gold))",
            boxShadow: "0 0 8px 2px hsla(var(--brand-gold),0.6)",
            animationDelay: s.delay,
          }}
        />
      ))}

      {/* Drifting / falling petals */}
      {PETALS.map((p, i) => (
        <div
          key={`petal-${i}`}
          className="petal-fall absolute top-0"
          style={{ left: p.left, animationDelay: p.delay, animationDuration: p.dur }}
        >
          <Petal size={p.size} color={p.color} />
        </div>
      ))}

      {/* Readability scrim + bottom fade into the page */}
      <div className="hero-illustration-scrim" />
      <div
        className="absolute inset-x-0 bottom-0 h-28"
        style={{ background: "linear-gradient(to bottom, hsla(var(--brand-paper),0) 0%, hsl(var(--brand-paper)) 100%)" }}
      />
    </div>
  );
};
